import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./nodeTypes";
import DataEdge from "./edgeTypes/DataEdge";
import ExecEdge from "./edgeTypes/ExecEdge";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import type { NodeTemplate } from "@features/flows/palette/registry";
import { inferTypes, applyInferredTypesToNodes } from "@features/flows/lib/type-infer";

// Local template map for object.get with shapePath inference
function buildTemplates(): Map<string, NodeTemplate> {
  const map = new Map<string, NodeTemplate>();
  map.set("core.object.get", {
    key: "core.object.get",
    title: "Object • Get",
    category: "Object",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [{ id: "object", type: "object" }], out: [{ id: "value", type: "any" }] },
    },
    attrsSchema: { type: "object", properties: { path: { type: "string" } } },
    pkg: "core",
    typeLinks: [
      {
        multiFrom: [
          { io: "in", id: "object", op: { name: "shapePath", pathAttr: "path", delimiter: ".", maxDepth: 32, arrayPolicy: "map" } } as any,
          { io: "in", id: "object", op: { name: "shapePath", pathAttr: "key", delimiter: ".", maxDepth: 32, arrayPolicy: "map" } } as any,
          { io: "in", id: "object", op: "value" } as any,
        ] as any,
        to: { io: "out", id: "value" },
        op: "prefer",
      } as any,
    ],
  } as any);
  return map;
}

type Props = {
  path?: string;
  withArray?: boolean;
};

function ShapePathPreview({ path = "user.address.city", withArray = false }: Props) {
  const templates = React.useMemo(buildTemplates, []);

  const objectNode: Node = {
    id: "obj",
    type: "default",
    position: { x: 60, y: 120 },
    data: {
      label: withArray ? "Object (users: Array<shape>)" : "Object (shape)",
      pins: {
        exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
        data: {
          in: [],
          out: [
            withArray
              ? { id: "value", type: "object", typeExpr: { tag: "shape", props: { users: { tag: "array", elem: { tag: "shape", props: { name: { tag: "coarse", name: "string" } } } } } } as any }
              : { id: "value", type: "object", typeExpr: { tag: "shape", props: { user: { tag: "shape", props: { address: { tag: "shape", props: { city: { tag: "coarse", name: "string" } } } } } } } as any },
          ],
        },
      },
    },
  };

  const getNode: Node = {
    id: "get",
    type: "default",
    position: { x: 420, y: 120 },
    data: {
      label: "core.object.get",
      templateKey: "core.object.get",
      pins: {
        exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
        data: {
          in: [{ id: "object", type: "object" }],
          out: [{ id: "value", type: "any" }],
        },
      },
      attrs: { path },
    },
  };

  const edges: Edge[] = [
    {
      id: "e-data",
      type: "data",
      source: "obj",
      target: "get",
      sourceHandle: "data:out:value",
      targetHandle: "data:in:object",
      data: { channel: "data", rawSourceHandle: "value", rawTargetHandle: "object" },
    } as any,
  ];

  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);

  const baseNodes = [objectNode, getNode];
  const baseEdges = edges;
  const [nodes, setNodes] = React.useState<Node[]>(baseNodes);
  const [edgs, setEdgs] = React.useState<Edge[]>(baseEdges);

  React.useEffect(() => {
    const n0 = [
      objectNode,
      { ...getNode, data: { ...(getNode.data as any), attrs: { path } } } as Node,
    ];
    try {
      const inferred = inferTypes(n0 as any, baseEdges as any, templates);
      const nextNodes = applyInferredTypesToNodes(n0 as any, inferred) as any as Node[];
      setNodes(nextNodes);
      setEdgs(baseEdges);
    } catch {
      setNodes(n0);
      setEdgs(baseEdges);
    }
  }, [path, withArray, templates]);

  return (
    <div className="h-[520px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edgs}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/TypeInference.shapePath",
  title: "TypeInference/shapePath",
  group: "Flows/Typing",
  component: ShapePathPreview as any,
  description: "Path-aware projection from shape/array with fallback.",
  defaultProps: { path: "user.address.city", withArray: false },
  controls: { path: { type: "text", label: "Path" }, withArray: { type: "boolean", label: "Array demo" } },
  variants: [
    { name: "City", props: { path: "user.address.city" } },
    { name: "Unknown (→ ANY)", props: { path: "user.address.zip" } },
    { name: "Array map", props: { withArray: true, path: "users.name" } },
  ],
};

export default meta;
